import {
  createInspectorFinding,
  isReportableFinding,
  withFindingEdit,
  withFindingEvidenceLink,
  withFindingStatus,
  withoutFindingEvidenceLink,
  type FindingEdit,
  type FindingStatus,
  type NewFindingInput,
  type V2Finding
} from '../domain/finding';
import { putRecords, type RecordWrite } from '../persistence/v2Database';
import { createStore } from './createStore';
import { assertInspectionUnlocked, prepareInspectionTouch } from './inspectionStore';

// Finding state — the OTHER FINDINGS of an area (issues outside the checklist, voluntary observations),
// separate from the inspector's working notes on the rows. Since the Final Owner Decision of
// 2026-09-14 (CA-15) no finding hangs on a checklist row: a row's Defect is its result, and the report
// derives the row's defect entry from the row itself. Every write also touches the parent inspection
// inside the same transaction.
export interface FindingState {
  hydrated: boolean;
  findings: V2Finding[];
}

export const findingStore = createStore<FindingState>({ hydrated: false, findings: [] });

export function resetFindingStoreForTests(): void {
  findingStore.set(() => ({ hydrated: false, findings: [] }));
}

export function applyFindingSnapshot(findings: V2Finding[]): void {
  findingStore.set(() => ({ hydrated: true, findings: [...findings] }));
}

export function removeFindingsOfInspectionLocal(inspectionId: string): void {
  findingStore.set((prev) => ({ ...prev, findings: prev.findings.filter((f) => f.inspectionId !== inspectionId) }));
}

function upsertLocal(next: V2Finding): void {
  findingStore.set((prev) => {
    const exists = prev.findings.some((f) => f.id === next.id);
    return {
      ...prev,
      findings: exists ? prev.findings.map((f) => (f.id === next.id ? next : f)) : [...prev.findings, next]
    };
  });
}

// ---------- selectors ----------

export function findingsOfArea(state: FindingState, areaId: string): V2Finding[] {
  return state.findings.filter((f) => f.areaId === areaId);
}

// "Other findings" of an area — every finding record there is (CA-15).
export function otherFindingsOfArea(state: FindingState, areaId: string): V2Finding[] {
  return state.findings.filter((f) => f.areaId === areaId);
}

export function reportableFindingsOf(state: FindingState, inspectionId: string): V2Finding[] {
  return state.findings.filter((f) => f.inspectionId === inspectionId && isReportableFinding(f));
}

// Every finding of an inspection (drafts, confirmed, withdrawn) — the Findings tab lists them all,
// labelled; only confirmed ones are reportable.
export function findingsOfInspection(state: FindingState, inspectionId: string): V2Finding[] {
  return state.findings.filter((f) => f.inspectionId === inspectionId);
}

// ---------- actions ----------

async function persist(next: V2Finding, now: number, extraWrites: RecordWrite[] = []): Promise<void> {
  assertInspectionUnlocked(next.inspectionId);
  const touch = prepareInspectionTouch(next.inspectionId, now);
  const writes: RecordWrite[] = [{ store: 'findings', value: next }, ...extraWrites];
  if (touch.write) writes.push(touch.write);
  await putRecords(writes);
  upsertLocal(next);
  touch.apply();
}

export async function addFindingAction(input: NewFindingInput, now: number = Date.now()): Promise<V2Finding | null> {
  // A local draft may start from either text field; a committed finding needs a title (the editor
  // additionally requires an observation before Save Finding, D-16).
  const draftContent = input.status === 'draft' && (input.observation ?? '').trim().length > 0;
  if (input.title.trim().length === 0 && !draftContent) return null;
  const finding = createInspectorFinding(input, now);
  await persist(finding, now);
  return finding;
}

// NY F1 Round 5 (Owner ruling OD-13, 2026-09-09 / agreement v2 §3.5): the quick "已看过 · 未见问题"
// entry. The inspector looked at something the New York Standards of Practice do not require and
// records what was seen — an INFORMATIONAL other finding, marked as a voluntary observation. It
// prints in the report's own "Other observations" block, never among the findings of the area, and
// is never counted as an observed component. The wording is the inspector's: this action stores what
// it is given (the screen offers the localized "Looked — no issue observed" as the starting text).
export interface VoluntaryObservationInput {
  inspectionId: string;
  areaId: string;
  // What was looked at.
  title: string;
  // What was seen.
  observation: string;
  evidenceIds?: string[];
}

export function addVoluntaryObservationAction(input: VoluntaryObservationInput, now: number = Date.now()): Promise<V2Finding | null> {
  return addFindingAction(
    {
      inspectionId: input.inspectionId,
      areaId: input.areaId,
      title: input.title,
      observation: input.observation,
      findingClass: 'informational',
      voluntaryObservation: true,
      evidenceIds: input.evidenceIds,
      status: 'confirmed'
    },
    now
  );
}

// Voluntary observations of one area (the report's "Other observations" block).
export function voluntaryObservationsOfArea(state: FindingState, areaId: string): V2Finding[] {
  return state.findings.filter((f) => f.areaId === areaId && f.voluntaryObservation);
}

export async function updateFindingAction(findingId: string, edit: FindingEdit, now: number = Date.now()): Promise<V2Finding | null> {
  const current = findingStore.get().findings.find((f) => f.id === findingId);
  if (!current) return null;
  const next = withFindingEdit(current, edit, now);
  await persist(next, now);
  return next;
}

export async function setFindingStatusAction(findingId: string, status: FindingStatus, now: number = Date.now()): Promise<V2Finding | null> {
  const current = findingStore.get().findings.find((f) => f.id === findingId);
  if (!current) return null;
  const next = withFindingStatus(current, status, now);
  await persist(next, now);
  return next;
}

// Explicit Save Finding (D-15): the record becomes a committed finding — entering the local Report
// Draft immediately — and any pending edits travel in the same write.
export async function commitFindingAction(findingId: string, edit: FindingEdit = {}, now: number = Date.now()): Promise<V2Finding | null> {
  const current = findingStore.get().findings.find((f) => f.id === findingId);
  if (!current) return null;
  const next = withFindingStatus(withFindingEdit(current, edit, now), 'confirmed', now);
  await persist(next, now);
  return next;
}

export async function deleteFindingAction(findingId: string, now: number = Date.now()): Promise<void> {
  const current = findingStore.get().findings.find((f) => f.id === findingId);
  if (!current) return;
  assertInspectionUnlocked(current.inspectionId);
  const touch = prepareInspectionTouch(current.inspectionId, now);
  await putRecords(touch.write ? [touch.write] : [], [{ store: 'findings', key: findingId }]);
  findingStore.set((prev) => ({ ...prev, findings: prev.findings.filter((f) => f.id !== findingId) }));
  touch.apply();
}

export function linkEvidenceToFindingAction(findingId: string, evidenceId: string, now: number = Date.now()) {
  const current = findingStore.get().findings.find((f) => f.id === findingId);
  if (!current) return Promise.resolve(null);
  const next = withFindingEvidenceLink(current, evidenceId, now);
  if (next === current) return Promise.resolve(current);
  return persist(next, now).then(() => next);
}

export function unlinkEvidenceFromFindingAction(findingId: string, evidenceId: string, now: number = Date.now()) {
  const current = findingStore.get().findings.find((f) => f.id === findingId);
  if (!current) return Promise.resolve(null);
  const next = withoutFindingEvidenceLink(current, evidenceId, now);
  if (next === current) return Promise.resolve(current);
  return persist(next, now).then(() => next);
}

// Used by the evidence flow to include finding link updates inside its own transaction.
export function prepareFindingEvidenceLink(findingId: string, evidenceId: string, now: number): { write: RecordWrite | null; apply: () => void } {
  const current = findingStore.get().findings.find((f) => f.id === findingId);
  if (!current) return { write: null, apply: () => undefined };
  const next = withFindingEvidenceLink(current, evidenceId, now);
  if (next === current) return { write: null, apply: () => undefined };
  return { write: { store: 'findings', value: next }, apply: () => upsertLocal(next) };
}

export function prepareFindingEvidenceUnlinks(evidenceId: string, now: number): { writes: RecordWrite[]; apply: () => void } {
  const affected = findingStore
    .get()
    .findings.filter((f) => f.evidenceIds.includes(evidenceId))
    .map((f) => withoutFindingEvidenceLink(f, evidenceId, now));
  return {
    writes: affected.map((value) => ({ store: 'findings' as const, value })),
    apply: () => {
      for (const f of affected) upsertLocal(f);
    }
  };
}
